import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  Typography,
  Stack,
  Backdrop,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import PaymentUploadDialog from './PaymentUploadDialog';
import { ILegalTransactionGwGFullObject } from '../../../Interfaces/ILegalTransactionGwG';
import { ILegalTransactionGwGEntry } from '../../../Interfaces/ILegalTransactionGwGEntry';
import { customFetcher } from '../../../hooks/useFetch';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';

interface PaymentTabProps {
  legalTransaction: {
    LegalTransactionGwGArray: ILegalTransactionGwGFullObject[];
  };
  idLegalTransaction: number;
  idContact: number;
}

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result || '');
      const i = s.indexOf(',');
      resolve(i >= 0 ? s.slice(i + 1) : s);
    };
    r.onerror = reject;
    r.readAsDataURL(file);
  });

// mapping (im Notafall)
const mimeToId = (mime: string): number => {
  switch (mime) {
    case 'application/pdf':
      return 4;
    case 'image/jpeg':
    case 'image/jpg':
      return 5;
    case 'image/png':
      return 6;
    default:
      return 4;
  }
};

const PaymentTab: React.FC<PaymentTabProps> = ({
  legalTransaction,
  idContact,
  idLegalTransaction,
}) => {
  const [gwgArray, setGwgArray] = useState<ILegalTransactionGwGFullObject[]>(
    legalTransaction.LegalTransactionGwGArray || []
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [parentIdForNew, setParentIdForNew] = useState<number | null>(null);

  const [attachTarget, setAttachTarget] = useState<{
    gwgId: number;
    entry: ILegalTransactionGwGEntry;
  } | null>(null);

  // Dialog zum Anzeigen der Dokument-Namen
  const [docDialogOpen, setDocDialogOpen] = useState(false);
  const [docDialogEntry, setDocDialogEntry] =
    useState<ILegalTransactionGwGEntry | null>(null);

  const openDocsDialog = (entry: ILegalTransactionGwGEntry) => {
    setDocDialogEntry(entry);
    setDocDialogOpen(true);
  };

  const [isUploading, setIsUploading] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackErr, setSnackErr] = useState<string | null>(null);
  const [snackMsg, setSnackMsg] = useState<string>('');

  const [feedback, setFeedback] = useState<{
    open: boolean;
    title: string;
    msg: string;
    severity: 'success' | 'error';
  }>({ open: false, title: '', msg: '', severity: 'success' });

  const handleAddPayment = (parentId: number) => {
    setAttachTarget(null);
    setParentIdForNew(parentId);
    setDialogOpen(true);
  };

  const handleAttachDocument = (
    gwgId: number,
    entry: ILegalTransactionGwGEntry
  ) => {
    setParentIdForNew(null);
    setAttachTarget({ gwgId, entry });
    setDialogOpen(true);
  };

  const getStateChip = (state?: number | null) => {
    const normalized = state ?? 1;

    const label =
      normalized === 1 ? 'Prüfung' : normalized === 2 ? 'Geprüft' : 'Abgelehnt';

    const color =
      normalized === 1 ? 'warning' : normalized === 2 ? 'success' : 'error';

    return { label, color };
  };

  const handleSaveDialog = async (
    file: File | null,
    meta: { title: string; amount: number }
  ) => {
    const isAttach = !!attachTarget;
    const gwgId = isAttach ? attachTarget!.gwgId : parentIdForNew;

    if (!gwgId) {
      setFeedback({
        open: true,
        title: 'Fehler',
        msg: 'GWG-Ziel fehlt.',
        severity: 'error',
      });
      return;
    }

    if (!file) {
      setFeedback({
        open: true,
        title: 'Hinweis',
        msg: 'Bitte eine Datei hochladen.',
        severity: 'error',
      });
      return;
    }

    setIsUploading(true);

    try {
      const base64 = await fileToBase64(file);

      const payload = {
        idContact,
        idLegalTransaction,
        Title: meta.title,
        Amount: meta.amount,
        idLegalTransactionGwG: gwgId,
        idLegalTransactionGwGEntry: isAttach
          ? attachTarget!.entry.idLegalTransactionGwGEntry
          : -1,
        Document: {
          Data: base64,
          FileName: file.name,
          idMIMEType: mimeToId(file.type),
        },
      };

      const res: { ok: boolean } = await customFetcher({
        endpoint: '/payment',
        method: 'POST',
        body: payload,
      });

      if (!res || !res.ok) {
        throw new Error('Upload fehlgeschlagen');
      }

      // Dialog schließen
      setDialogOpen(false);
      setParentIdForNew(null);
      setAttachTarget(null);

      // Bestätigungsdialog
      setFeedback({
        open: true,
        title: 'Dokument hochgeladen',
        msg: 'Ihr Dokument wird verarbeitet und in Kürze sichtbar.',
        severity: 'success',
      });
    } catch (e: any) {
      console.error('Fehler beim Speichern:', e);
      setFeedback({
        open: true,
        title: 'Dokument hochladen fehlgeschlagen',
        msg: e?.message || 'Upload fehlgeschlagen',
        severity: 'error',
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Box>
      {gwgArray.map(gwg => (
        <Box key={gwg.idLegalTransactionGwG} sx={{ mb: 4 }}>
          <Typography variant='h6' sx={{ mb: 1 }}>
            {gwg.title}
          </Typography>

          <Button
            variant='outlined'
            onClick={() => handleAddPayment(gwg.idLegalTransactionGwG)}
            sx={{ mt: 1 }}
          >
            Neue Zahlung hinzufügen
          </Button>

          <Grid container spacing={2} mt={2}>
            {/* Bisherige Zahlungen */}
            <Grid item xs={12} md={6}>
              <Typography variant='subtitle1'>Bisherige Zahlungen</Typography>
              {gwg.LegalTransactionGwGEntryArray.filter(
                e => e.currentRate === 0.0
              ).map(entry => {
                const { label, color } = getStateChip(
                  entry.idLegalTransactionGwGEntryState
                );
                const docCount = entry.DocumentArray?.length ?? 0;

                return (
                  <Box
                    key={entry.idLegalTransactionGwGEntry}
                    sx={{
                      position: 'relative',
                      border: '1px solid #ddd',
                      borderRadius: 1,
                      p: 1.25,
                      mb: 1.25,
                    }}
                  >
                    {/* Status oben rechts */}
                    <Chip
                      label={label}
                      size='small'
                      variant='outlined'
                      color={color as 'warning' | 'success' | 'error'}
                      sx={{ position: 'absolute', top: 8, right: 8 }}
                    />

                    {/* Zeile 1: Titel */}
                    <Typography variant='subtitle2' sx={{ pr: 10 }} noWrap>
                      {entry.title}
                    </Typography>

                    {/* Zeile 2: Betrag + Doku-Chip + Upload */}
                    <Stack
                      direction='row'
                      spacing={1}
                      alignItems='center'
                      sx={{ mt: 0.5, flexWrap: 'wrap' }}
                    >
                      <Typography variant='body2'>
                        Betrag: {entry.currentValue} €
                      </Typography>

                      <Tooltip title='Dokumente anzeigen'>
                        <span>
                          <Chip
                            icon={<InsertDriveFileIcon fontSize='small' />}
                            label={docCount}
                            size='small'
                            variant='outlined'
                            clickable
                            onClick={() => openDocsDialog(entry)}
                          />
                        </span>
                      </Tooltip>

                      <Tooltip title='Dokument hinzufügen'>
                        <span>
                          <IconButton
                            size='small'
                            onClick={() =>
                              handleAttachDocument(
                                gwg.idLegalTransactionGwG,
                                entry
                              )
                            }
                            aria-label='Dokument hinzufügen'
                          >
                            <FileUploadIcon fontSize='small' />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Box>
                );
              })}
            </Grid>

            {/* Bisherige Ablöseforderungen */}
            <Grid item xs={12} md={6}>
              <Typography variant='subtitle1'>
                Bisherige Ablöseforderungen
              </Typography>
              {gwg.LegalTransactionGwGEntryArray.filter(
                e => e.currentRate > 0.0
              ).map(entry => {
                const docCount = entry.DocumentArray?.length ?? 0;

                return (
                  <Box
                    key={entry.idLegalTransactionGwGEntry}
                    sx={{
                      position: 'relative',
                      border: '1px solid #ddd',
                      borderRadius: 1,
                      p: 1.25,
                      mb: 1.25,
                    }}
                  >
                    {/* Zeile 1: Titel */}
                    <Typography variant='subtitle2' noWrap>
                      {entry.title}
                    </Typography>

                    {/* Zeile 2: Betrag + Doku-Chip + Upload */}
                    <Stack
                      direction='row'
                      spacing={1}
                      alignItems='center'
                      sx={{ mt: 0.5, flexWrap: 'wrap' }}
                    >
                      <Typography variant='body2'>
                        Betrag: {entry.currentValue} €
                      </Typography>

                      <Tooltip title='Dokumente anzeigen'>
                        <span>
                          <Chip
                            icon={<InsertDriveFileIcon fontSize='small' />}
                            label={docCount}
                            size='small'
                            variant='outlined'
                            clickable
                            onClick={() => openDocsDialog(entry)}
                          />
                        </span>
                      </Tooltip>

                      <Tooltip title='Dokument hinzufügen'>
                        <span>
                          <IconButton
                            size='small'
                            onClick={() =>
                              handleAttachDocument(
                                gwg.idLegalTransactionGwG,
                                entry
                              )
                            }
                            aria-label='Dokument hinzufügen'
                          >
                            <FileUploadIcon fontSize='small' />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Box>
                );
              })}
            </Grid>
          </Grid>
        </Box>
      ))}

      {dialogOpen && (
        <PaymentUploadDialog
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            setParentIdForNew(null);
            setAttachTarget(null);
          }}
          onSave={handleSaveDialog}
          requireFile
          initialTitle={attachTarget?.entry.title ?? undefined}
          initialAmount={attachTarget?.entry.currentValue ?? undefined}
          lockMetaFields={!!attachTarget}
          dialogTitle={
            attachTarget
              ? 'Weiteres Dokument hinzufügen'
              : 'Neue Zahlung hinzufügen'
          }
        />
      )}

      {/* Dialog: Dokumentnamen anzeigen */}
      <Dialog
        open={docDialogOpen}
        onClose={() => setDocDialogOpen(false)}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle>Dokumente: {docDialogEntry?.title ?? ''}</DialogTitle>
        <DialogContent dividers>
          {!docDialogEntry?.DocumentArray?.length ? (
            <Typography variant='body2' color='text.secondary'>
              Keine Dokumente vorhanden.
            </Typography>
          ) : (
            <List dense>
              {docDialogEntry!.DocumentArray!.map(doc => (
                <ListItem key={doc.idDocument}>
                  <ListItemIcon>
                    <InsertDriveFileIcon fontSize='small' />
                  </ListItemIcon>

                  <ListItemText
                    primary={doc.FileName}
                    primaryTypographyProps={{ noWrap: true }}
                    secondary={
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          mt: 0.25,
                          opacity: 0.75,
                        }}
                      >
                        <PersonOutlineIcon sx={{ fontSize: 14, mr: 0.5 }} />
                        <Typography variant='caption' color='text.secondary'>
                          Hochgeladen von:{' '}
                          {typeof doc.uploaderName === 'string' &&
                          doc.uploaderName.trim() !== ''
                            ? doc.uploaderName.trim()
                            : 'Notariat'}
                        </Typography>
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDocDialogOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* Upload-Backdrop */}
      <Backdrop
        open={isUploading}
        sx={{ color: '#fff', zIndex: t => t.zIndex.modal + 2 }}
      >
        <CircularProgress />
      </Backdrop>

      {/* Feedback-Dialog*/}
      <Dialog
        open={feedback.open}
        onClose={() => setFeedback(f => ({ ...f, open: false }))}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>{feedback.title}</DialogTitle>
        <DialogContent dividers>
          <Alert severity={feedback.severity}>{feedback.msg}</Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setFeedback(f => ({ ...f, open: false }))}
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentTab;
